import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, PauseCircle, ShoppingBag } from "lucide-react";
import { Button, Card, Switch } from "../../../components";
import { useAuth } from "../../../context/AuthContext";
import { ONLINE_ORDERS_SETTINGS_URL, SELLER_DETAILS_URL } from "../../../features/UrlHelper";
import "./OnlineOrdersSection.scss";

/**
 * Nastavenia online objednávok (R35, R36, R57).
 *
 * Zapínať a pozastavovať smie ADMIN, SUPERADMIN aj MANAGER; údaje
 * predávajúceho mení len ADMIN — je to údaj, ktorým sa prevádzka predstavuje
 * v obchodných podmienkach.
 *
 * Prepínač sa **nezakazuje ticho**: keď sa zapnúť nedá, obrazovka povie prečo
 * a čo s tým. Server to isté kontroluje znova a vracia tú istú vetu, takže
 * sa hláška udržiava na jednom mieste.
 */

/** Čo sa pýtame o predávajúcom. `povinne` zhodne so serverom. */
const POLIA = [
  { key: "legal_name", label: "Obchodné meno", povinne: true, placeholder: "Veterán Pub s.r.o." },
  { key: "legal_address", label: "Sídlo", povinne: true, placeholder: "Hlavná 1, 010 01 Žilina" },
  { key: "ico", label: "IČO", povinne: true, placeholder: "12345678" },
  { key: "dic", label: "DIČ", povinne: false, placeholder: "2023456789" },
  { key: "ic_dph", label: "IČ DPH", povinne: false, placeholder: "Nechajte prázdne, ak nie ste platiteľ DPH" },
  { key: "legal_registration", label: "Zápis v registri", povinne: false, placeholder: "OR OS Žilina, odd. Sro, vl. 1234/L" },
];

const PRAZDNE = Object.fromEntries(POLIA.map((p) => [p.key, ""]));

function OnlineOrdersSection() {
  const { userRole } = useAuth();
  const jeAdmin = userRole === "ADMIN" || userRole === "SUPERADMIN";

  const [stav, setStav] = useState(null);
  const [udaje, setUdaje] = useState(PRAZDNE);
  const [nacitava, setNacitava] = useState(true);
  const [uklada, setUklada] = useState(false);
  const [chyba, setChyba] = useState("");
  const [hlaska, setHlaska] = useState("");

  const prevezmi = useCallback((data) => {
    setStav(data);
    setUdaje({ ...PRAZDNE, ...(data.udaje || {}) });
  }, []);

  useEffect(() => {
    axios
      .get(ONLINE_ORDERS_SETTINGS_URL)
      .then(({ data }) => prevezmi(data))
      .catch(() => setChyba("Nastavenia sa nepodarilo načítať."))
      .finally(() => setNacitava(false));
  }, [prevezmi]);

  const posli = async (url, telo, metoda = "patch") => {
    setUklada(true);
    setChyba("");
    setHlaska("");
    try {
      const { data } = await axios[metoda](url, telo);
      prevezmi(data);
      return true;
    } catch (err) {
      setChyba(err.response?.data?.message || "Zmenu sa nepodarilo uložiť.");
      return false;
    } finally {
      setUklada(false);
    }
  };

  const prepni = (zapnut) => posli(ONLINE_ORDERS_SETTINGS_URL, { enabled: zapnut });

  const pozastav = (pauza) => posli(ONLINE_ORDERS_SETTINGS_URL, { pauza });

  const ulozUdaje = async (e) => {
    e.preventDefault();
    if (await posli(SELLER_DETAILS_URL, udaje)) {
      setHlaska("Údaje predávajúceho sú uložené.");
    }
  };

  if (nacitava) {
    return (
      <div className="admin__section">
        <h2 className="admin__section-title">Objednávanie cez web</h2>
        <p>Načítavam...</p>
      </div>
    );
  }

  if (!stav) {
    return (
      <div className="admin__section">
        <h2 className="admin__section-title">Objednávanie cez web</h2>
        <p className="admin__form-error">{chyba || "Nastavenia sa nepodarilo načítať."}</p>
      </div>
    );
  }

  const pauzaDo = stav.pausedUntil ? new Date(stav.pausedUntil) : null;

  return (
    <div className="admin__section webobj">
      <h2 className="admin__section-title">Objednávanie cez web</h2>
      <p className="admin__form-hint">
        Keď sú zapnuté, zákazníci si vedia objednať sami na stránke prevádzky. Telefonické
        objednávky fungujú vždy, bez ohľadu na toto nastavenie.
      </p>

      {chyba && <p className="admin__form-error" role="alert">{chyba}</p>}

      {/* ── Prepínač ── */}
      <Card>
        <Card.Header>
          <Card.Title>Zapnutie a pozastavenie</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="webobj__prepinac">
            <Switch
              checked={stav.enabled}
              onChange={prepni}
              disabled={uklada || (!stav.enabled && !!stav.prekazka)}
              label={stav.enabled ? "Zapnuté" : "Vypnuté"}
            />
            {stav.enabled ? (
              <span className="webobj__stav webobj__stav--zapnute">
                <CheckCircle2 size={16} aria-hidden="true" /> Zákazníci si vedia objednať
              </span>
            ) : (
              <span className="webobj__stav">
                <ShoppingBag size={16} aria-hidden="true" /> Zákazníci si objednajú telefonicky
              </span>
            )}
          </div>

          {stav.prekazka && (
            <p className="webobj__prekazka" role="status">
              <AlertTriangle size={17} aria-hidden="true" />
              {stav.prekazka}
            </p>
          )}

          {stav.enabled && (
            <div className="webobj__pauza">
              <span className="admin__form-hint">
                Dočasná pauza sa hodí, keď vám napríklad dôjdu suroviny. Zapnuté nastavenie
                sa ňou nemení, objednávky sa len na chvíľu neprijímajú.
              </span>
              {stav.pozastavene ? (
                <div className="webobj__pauza-aktivna">
                  <span className="webobj__stav webobj__stav--pauza">
                    <PauseCircle size={16} aria-hidden="true" /> Pozastavené do{" "}
                    {pauzaDo?.toLocaleString("sk-SK", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "numeric" })}
                  </span>
                  <Button variant="outline" onClick={() => pozastav(null)} disabled={uklada}>
                    Pokračovať v prijímaní
                  </Button>
                </div>
              ) : (
                <div className="webobj__pauza-volby">
                  <Button variant="outline" onClick={() => pozastav("HODINA")} disabled={uklada}>
                    Pozastaviť na hodinu
                  </Button>
                  <Button variant="outline" onClick={() => pozastav("DO_KONCA_DNA")} disabled={uklada}>
                    Pozastaviť do konca dňa
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Výdajná trasa (R57) ── */}
      <Card>
        <Card.Header>
          <Card.Title>Trasa výdaja na prevádzke</Card.Title>
        </Card.Header>
        <Card.Body>
          {stav.vydajnaTrasa ? (
            <p className="webobj__stav webobj__stav--zapnute">
              <CheckCircle2 size={16} aria-hidden="true" />
              Používa sa trasa <strong>{stav.vydajnaTrasa.name}</strong>
            </p>
          ) : (
            <p className="webobj__stav webobj__stav--chyba">
              <AlertTriangle size={16} aria-hidden="true" /> Zatiaľ žiadna
            </p>
          )}
          <p className="admin__form-hint">
            Na túto trasu idú objednávky, po ktoré si zákazník príde sám. Ide na ňu aj{" "}
            <strong>prvá objednávka každého nového zákazníka</strong> — kým mu v jeho karte
            nepridelíte rozvozovú trasu, donášku cez web nedostane. Založíte ju v{" "}
            <em>Správe trás</em> zaškrtnutím „Výdaj na prevádzke“.
          </p>
        </Card.Body>
      </Card>

      {/* ── Údaje predávajúceho (R36) ── */}
      <Card>
        <Card.Header>
          <Card.Title>Údaje predávajúceho</Card.Title>
        </Card.Header>
        <Card.Body>
          <p className="admin__form-hint">
            Idú do obchodných podmienok. Keď si zákazník objedná sám, vzniká spotrebiteľská
            zmluva a musí byť jasné, s kým ju uzatvára.
            {!jeAdmin && " Meniť ich môže len administrátor prevádzky."}
          </p>

          <form className="admin__form" onSubmit={ulozUdaje}>
            {POLIA.map((pole) => (
              <div className="admin__form-field" key={pole.key}>
                <label className="admin__form-label" htmlFor={`predavajuci-${pole.key}`}>
                  {pole.label}
                  {pole.povinne && <span className="webobj__povinne"> *</span>}
                </label>
                <input
                  id={`predavajuci-${pole.key}`}
                  className="admin__form-input"
                  value={udaje[pole.key] || ""}
                  onChange={(e) => setUdaje((p) => ({ ...p, [pole.key]: e.target.value }))}
                  placeholder={pole.placeholder}
                  disabled={!jeAdmin || uklada}
                />
              </div>
            ))}

            {hlaska && <p className="admin__form-success">{hlaska}</p>}

            {jeAdmin && (
              <div className="admin__form-actions">
                <Button type="submit" variant="primary" disabled={uklada}>
                  {uklada ? "Ukladám..." : "Uložiť údaje"}
                </Button>
              </div>
            )}
          </form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default OnlineOrdersSection;
